import React, { useReducer, useEffect, useContext } from 'react'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { get as _get } from 'lodash'
import sectionAccordionReducer, {
  buildGetActionsForAccordion,
  buildGlobalAccordionActions,
  DEFAULT_ACCORDION_ACTIONS,
  AccordionState,
  AccordionReducer,
  AccordionActions,
} from './sectionAccordionReducer'
import { useTheme } from '@material-ui/core/styles'

const DEFAULT_INITIAL_STATE = {
  isAccordionOpen: false,
  isAccordionEnabled: false,
}

type SectionAccordionState = {
  state: AccordionReducer
  getAccordionState(id: string): AccordionState | null
  getAccordionActions(id: string): AccordionActions
}

const useSetAccordionStateForBreakpoint = (
  enableAllAccordions,
  disableAllAccordions
) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isPrint = useMediaQuery('print')
  const shouldDisableAllAccordions = isDesktop || isPrint

  useEffect(() => {
    if (shouldDisableAllAccordions) {
      disableAllAccordions()
    } else {
      enableAllAccordions()
    }
  }, [shouldDisableAllAccordions])
}

const useSectionAccordionState = (): SectionAccordionState => {
  const [state, dispatch] = useReducer(sectionAccordionReducer, {})
  const getAccordionState = (id: string): AccordionState | null =>
    _get(state, id, null)
  const getAccordionActions = (id: string): AccordionActions =>
    buildGetActionsForAccordion(dispatch, id)
  const { enableAllAccordions, disableAllAccordions } =
    buildGlobalAccordionActions(dispatch)

  useSetAccordionStateForBreakpoint(enableAllAccordions, disableAllAccordions)

  return { state, getAccordionState, getAccordionActions }
}

const SectionAccordionContext = React.createContext<SectionAccordionState>({
  state: {},
  getAccordionState: () => DEFAULT_INITIAL_STATE,
  getAccordionActions: () => DEFAULT_ACCORDION_ACTIONS,
})

const SectionAccordionProvider = ({ children }) => {
  const value = useSectionAccordionState()

  return (
    <SectionAccordionContext.Provider value={value}>
      {children}
    </SectionAccordionContext.Provider>
  )
}

export const useSectionAccordion = (
  id: string,
  initialState: AccordionState = { ...DEFAULT_INITIAL_STATE }
) => {
  const { getAccordionState, getAccordionActions } = useContext(
    SectionAccordionContext
  )
  const state = getAccordionState(id)
  const accordionActions = getAccordionActions(id)

  useEffect(() => {
    if (state === null) {
      accordionActions.registerAccordion(initialState)
    }
  }, [state])

  const accordionProps: AccordionState = state || initialState

  return {
    accordionProps,
    ...accordionActions,
  }
}

export default SectionAccordionProvider
